import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import { formatLongDate, formatDuration, ItemHeroIcon, itemTitle } from "../checkoutFormatting";

// `embedded` drops the standalone card so the booking can sit as a section above
// the policies inside one card (mobile). `position: relative` anchors the mobile
// icon column, which is absolutely positioned, so it stays on either wrapper.
function CheckoutItemCard({ item, displayCurrency, sportIcon, onRemove, bookingForName, embedded = false }) {
  const subtotal = parseFloat(item.price) || 0;
  // A claimed spot on someone else's split booking. The price on the card is one share, not
  // the court, so the card has to say so — otherwise the amount reads as the whole booking.
  const split = item.split;
  const courtTotal = parseFloat(split?.totalAmount) || 0;

  const Wrapper = embedded ? Box : Paper;
  const wrapperProps = embedded
    ? { sx: { position: "relative", p: { xs: 2.5, md: 3 } } }
    : {
        elevation: 0,
        sx: {
          position: "relative",
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: 1,
          borderColor: (theme) => alpha(theme.palette.divider, 0.6),
          backgroundColor: "background.paper"
        }
      };

  return (
    <Wrapper {...wrapperProps}>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: { xs: "flex-start", md: "stretch" }, gap: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            width: { xs: "3.5rem", md: "10rem" },
            borderRadius: 2,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
            color: "primary.main",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& svg": { fontSize: "3.5rem" }
          }}
        >
          <ItemHeroIcon sportIcon={sportIcon} type={item.type} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 2 }}>
          <Box>
            {embedded ? (
              // Sharing a card with the policies section below, so it takes the
              // same tinted-icon heading rather than a bare title.
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: "2.25rem",
                      height: "2.25rem",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      "& svg": { fontSize: "1.25rem" }
                    }}
                  >
                    <ItemHeroIcon sportIcon={sportIcon} type={item.type} size={20} />
                  </Box>
                  {/* Wraps rather than truncates: this is the screen where the client
                      confirms what they're paying for, so the service name has to be
                      readable in full. minWidth 0 lets it shrink to the phone — without
                      it a long name sets the card's min-content width and pushes the
                      delete button and the subtotal off the right edge. */}
                  <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", minWidth: 0, overflowWrap: "break-word" }}>
                    {itemTitle(item)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={onRemove}
                  sx={{
                    flexShrink: 0,
                    color: "error.main",
                    "&:hover": { color: "error.dark", backgroundColor: (t) => alpha(t.palette.error.main, 0.08) }
                  }}
                  aria-label="Remove item"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1.5, pr: { xs: 6, md: 0 } }}>
                <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary" }}>{itemTitle(item)}</Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                    position: { xs: "absolute", md: "static" },
                    top: { xs: 20 },
                    right: { xs: 20 }
                  }}
                >
                  <Box sx={{ display: { xs: "flex", md: "none" }, color: "primary.main", "& svg": { fontSize: "2.5rem" } }}>
                    <ItemHeroIcon sportIcon={sportIcon} type={item.type} size={40} />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={onRemove}
                    sx={{ color: "error.main", "&:hover": { color: "error.dark", backgroundColor: (t) => alpha(t.palette.error.main, 0.08) } }}
                    aria-label="Remove item"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            <Stack spacing={1.25}>
              {item.date && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: "1rem" }} />
                  <Typography variant="body2">{formatLongDate(item.date)}</Typography>
                </Box>
              )}
              {item.time && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: "1rem" }} />
                  <Typography variant="body2">
                    {item.time}
                    {item.duration ? ` • ${formatDuration(item.duration)}` : ""}
                  </Typography>
                </Box>
              )}
              {(item.facilityName || item.locationName) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                  <Typography variant="body2">
                    {item.facilityName}
                    {item.facilityName && item.locationName ? " • " : ""}
                    {item.locationName ? (
                      <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                        {item.locationName}
                      </Box>
                    ) : null}
                  </Typography>
                </Box>
              )}
              {item.trainerName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                  <PersonOutlineIcon sx={{ fontSize: "1rem" }} />
                  <Typography variant="body2">{item.trainerName}</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Box>
            <Divider sx={{ mb: 1.5, opacity: 0.5 }} />
            {bookingForName && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  p: 1,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06)
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: "1.125rem", color: "primary.main" }} />
                <Typography variant="body2" color="text.secondary">
                  Booking for{" "}
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                    {bookingForName}
                  </Box>
                </Typography>
              </Box>
            )}
            {split && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  p: 1,
                  px: 1.25,
                  borderRadius: 2,
                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12)
                }}
              >
                <GroupsOutlinedIcon sx={{ fontSize: "1.125rem", color: "warning.dark", flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                    Split booking
                  </Box>
                  {` — your share of ${split.seatsTotal} spots`}
                  {courtTotal > 0 ? ` · court total ${displayCurrency}${courtTotal.toFixed(2)}` : ""}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "text.secondary"
                }}
              >
                {split ? "Your share" : "Subtotal"}
              </Typography>
              <Typography sx={{ fontSize: "1.375rem", fontWeight: 600, color: "primary.main" }}>
                {displayCurrency}
                {subtotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Wrapper>
  );
}

export default CheckoutItemCard;
